import { useTranslation, Trans } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, X } from "lucide-react";

interface NewsFiltersProps {
  categories: string[];
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
  query: string;
  onQueryChange: (query: string) => void;
  resultCount: number;
  totalCount: number;
  onClear: () => void;
}

const ALL = "All";

const NewsFilters = ({
  categories,
  selectedCategory,
  onCategoryChange,
  query,
  onQueryChange,
  resultCount,
  totalCount,
  onClear,
}: NewsFiltersProps) => {
  const { t } = useTranslation();
  const isFiltered = selectedCategory !== ALL || query.trim().length > 0;

  const labelForCategory = (cat: string) =>
    cat === ALL
      ? t("newsPage.filters.all")
      : t(`newsPage.categories.${cat}`, { defaultValue: cat });

  return (
    <div className="mb-10 space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        {/* Category chips */}
        <div className="flex flex-wrap gap-2" role="group" aria-label={t("newsPage.filters.ariaCategory")}>
          {[ALL, ...categories].map((cat) => {
            const active = cat === selectedCategory;
            return (
              <Button
                key={cat}
                size="sm"
                variant={active ? "primary" : "secondary"}
                onClick={() => onCategoryChange(cat)}
                aria-pressed={active}
              >
                {labelForCategory(cat)}
              </Button>
            );
          })}
        </div>

        {/* Search */}
        <div className="relative w-full lg:w-72 shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <Input
            type="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder={t("newsPage.filters.searchPlaceholder")}
            className="pl-9 pr-9"
            aria-label={t("newsPage.filters.searchAria")}
          />
          {query && (
            <button
              type="button"
              onClick={() => onQueryChange("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              aria-label={t("newsPage.filters.clearSearchAria")}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>
          <Trans
            i18nKey="newsPage.filters.showingCount"
            values={{ count: resultCount, total: totalCount }}
            components={{ 1: <span className="text-foreground font-medium" /> }}
          />
        </span>
        {isFiltered && (
          <Button variant="tertiary" size="sm" onClick={onClear}>
            {t("newsPage.filters.clearFilters")}
          </Button>
        )}
      </div>
    </div>
  );
};

export default NewsFilters;
